//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import equal from 'fast-deep-equal';
import React, { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';
import { O } from 'ts-toolbelt';

import { BodySecondaryText } from '@components/atoms/text/Text';
import DropdownButton from '@components/molecules/button-popover/DropdownButton';
import {
  DropdownSelectSearch,
  DropdownSelectSearchConfig,
} from '@components/molecules/dropdown-select/DropdownSelectSearch';
import { Select } from '@components/primitives/select/Select';
import { useUniqueId } from '@hooks/useUniqueId';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';

export type SelectItemValue = string | number | object;

export interface IDropdownSelectItem<T extends SelectItemValue> {
  caption?: string;
  formatLabel?: () => ReactNode;
  isDisabled?: boolean;
  label: string;
  trackingId?: string;
  value: T | undefined;
}

type ButtonProps = O.Optional<
  Omit<
    React.ComponentProps<typeof DropdownButton>,
    'valueText' | 'rightContent'
  >,
  'placeholderText'
>;

export interface DropdownSelectProps<T extends SelectItemValue>
  extends ButtonProps {
  className?: string;
  dropdownContentZIndex?: string;
  handleSelect: (value: T) => void;
  items: IDropdownSelectItem<T>[];
  label?: string;
  labelTextMixin?: string;
  maxHeight?: number;
  noItemsContent?: React.ReactElement;
  searchBarConfig?: DropdownSelectSearchConfig;
  trackingId?: string;
  value: T | undefined;
  width?: number;
}

/**
 * The DropdownSelect molecule renders a DropDownButton
 * and an associated Select using the default styles
 * and props for both.
 */
export const DropdownSelect = <T extends SelectItemValue>({
  className,
  dropdownContentZIndex,
  handleSelect,
  items,
  label,
  labelTextMixin,
  maxHeight,
  noItemsContent,
  placeholderText = 'Select a Value',
  searchBarConfig,
  trackingId,
  value,
  width,
  ...buttonProps
}: DropdownSelectProps<T>) => {
  const [options, setOptions] = useState(items);
  const [searchValue, setSearchValue] = useState('');
  const dropdownId = useUniqueId('dropdown-select-label-id');

  const indexedOptions = options.map((item) => ({
    ...item,
    id: JSON.stringify(item.value),
  }));

  const selectedOption = indexedOptions.find((item) =>
    equal(item.value, value),
  );
  const selectedLabel = (() => {
    if (selectedOption) {
      if (isDefined(selectedOption.formatLabel)) {
        return selectedOption.formatLabel();
      } else {
        const caption = selectedOption.caption
          ? ` | ${selectedOption.caption}`
          : '';
        return `${selectedOption.label}${caption}`;
      }
    }
  })();

  return (
    <Wrapper>
      {label && (
        <LabelWrapper htmlFor={dropdownId} mixin={labelTextMixin}>
          {label}
        </LabelWrapper>
      )}
      <Select
        disabled={buttonProps.isDisabled}
        onValueChange={(id) => {
          const selectedItem = indexedOptions.find((item) =>
            equal(item.id, id),
          );
          if (isDefined(selectedItem?.value)) {
            handleSelect(selectedItem.value);
          }
        }}
        value={selectedOption?.id}
      >
        <Select.Trigger asChild>
          <DropdownButton
            className={className}
            id={dropdownId}
            isDropdown={true}
            placeholderText={placeholderText}
            trackingId={trackingId}
            valueText={selectedLabel}
            {...buttonProps}
          />
        </Select.Trigger>
        <StyledSelectContent
          align='start'
          dropdownContentZIndex={dropdownContentZIndex}
          maxHeight={maxHeight}
          width={width}
        >
          {items.length === 0 && (
            <NoContentWrapper>{noItemsContent}</NoContentWrapper>
          )}
          {items.length > 0 && searchBarConfig && (
            <DropdownSelectSearch
              handleClear={() => setOptions([...items])}
              handleSearch={(term, filteredItems) =>
                setOptions(term === '' ? [...items] : [...filteredItems])
              }
              itemsToSearch={items}
              searchBarConfig={searchBarConfig}
              searchKeys={['label']}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />
          )}
          {indexedOptions.map((option) => (
            <Select.Item
              disabled={option.isDisabled}
              key={option.id}
              trackingId={option.trackingId}
              value={option.id}
            >
              <SelectItemText>
                <Select.ItemText>
                  {isDefined(option.formatLabel)
                    ? option.formatLabel()
                    : option.label}
                </Select.ItemText>
                {option.caption && (
                  <Select.ItemText>
                    <BodySecondaryText>{option.caption}</BodySecondaryText>
                  </Select.ItemText>
                )}
              </SelectItemText>
              <Select.Indicator />
            </Select.Item>
          ))}
        </StyledSelectContent>
      </Select>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const NoContentWrapper = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[2]};
`,
);

const LabelWrapper = styled.label<{
  mixin?: string;
}>(
  ({ mixin, theme }) => css`
    ${mixin ?? Mixins.Text.UiCopy};
    display: flex;
    align-items: center;
    margin-bottom: ${theme.spacings[2]};
  `,
);

const StyledSelectContent = styled(Select.Content)<{
  dropdownContentZIndex?: string;
  maxHeight?: number;
  width?: number;
}>(
  ({ dropdownContentZIndex, maxHeight, theme, width }) => `
  ${maxHeight ? `max-height: ${maxHeight}px;` : ''}
  width: ${width ? `${width}px` : theme.smallMeasure};
  overflow-y: auto;
  ${isDefined(dropdownContentZIndex) && `z-index: ${dropdownContentZIndex};`} // To ensure that when the dropdown menu is opened it is displayed on top of any modals (e.g. gallery customization editor modal)
`,
);

const SelectItemText = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
`,
);

export default DropdownSelect;
